import { FunctionComponent } from 'react'
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form'
import { TrackingLabel } from '../types/tracking'
import { WithClassName } from '../types/utils'
import { Icon, IconName } from './Icon'
import { cn } from '../utils/cn'

type RadioOptionValueType = string | number | boolean | object
type ComponentSize = 'normal' | 'small'

const RADIO_ITEM_SIZE: Record<ComponentSize, string> = {
  normal: 'py-1 px-2.25',
  small: 'py-1 px-2.5 text-sm',
}

export type RadioOption = {
  value: RadioOptionValueType
  label: string | React.ReactNode
  trackingLabel: TrackingLabel
  disabled?: boolean
  iconName?: IconName
}

type BaseRadioGroupProps = WithClassName & {
  options: RadioOption[]
  value?: RadioOptionValueType
  onChange: (value: RadioOptionValueType) => void
  size?: ComponentSize
  variant?: 'primary' | 'secondary'
}

/* ---------------- WRAPPER ---------------- */

const RadioGroupWrapper: FunctionComponent<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <div className={cn('inline-flex flex-row pr-px bg-white rounded', className)}>{children}</div>
)

/* ---------------- ITEM ---------------- */

const RadioItem: FunctionComponent<{
  selected: boolean
  disabled: boolean
  size?: ComponentSize
  variant: 'primary' | 'secondary'
  isFirst: boolean
  isLast: boolean
  trackingLabel: TrackingLabel
  onClick?: () => void
  children: React.ReactNode
}> = ({ selected, disabled, size = 'normal', variant, isFirst, isLast, trackingLabel, onClick, children }) => (
  <div
    data-tracking={trackingLabel}
    data-disabled={disabled}
    onClick={!disabled ? onClick : undefined}
    className={cn(
      'flex flex-row gap-1 items-center border bg-clip-padding -mr-px transition-all duration-150 ease-in-out',
      RADIO_ITEM_SIZE[size],
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      isFirst && 'rounded-l',
      isLast && 'rounded-r',
      disabled && 'bg-cloud border-transparent text-[#767676]',
      !disabled && variant === 'primary' && (selected ? 'bg-king hover:bg-cobalt text-white border-king' : 'bg-white text-king border-platinum border-1 hover:border-cobalt'),
      !disabled && variant === 'secondary' && (selected ? 'bg-white hover:bg-cloud text-king border-king' : 'bg-white hover:bg-cobalt text-king border-transparent hover:border-cobalt')
    )}
  >
    {children}
  </div>
)

export const BaseRadioGroup: FunctionComponent<BaseRadioGroupProps> = ({ className, variant = 'primary', options, value, onChange, size }) => {
  return (
    <RadioGroupWrapper className={className}>
      {options.map((option, index) => {
        const selected = JSON.stringify(option.value) === JSON.stringify(value)

        return (
          <RadioItem
            key={option.trackingLabel + index}
            variant={variant}
            selected={selected}
            disabled={!!option.disabled}
            size={size}
            isFirst={index === 0}
            isLast={index === options.length - 1}
            trackingLabel={option.trackingLabel}
            onClick={() => onChange(option.value)}
          >
            {option.iconName && <Icon size={size === 'small' ? 'sm' : 'md'} name={option.iconName} />}
            {option.label}
          </RadioItem>
        )
      })}
    </RadioGroupWrapper>
  )
}

type RadioGroupProps<T extends FieldValues> = Omit<UseControllerProps<T>, 'defaultValue'> &
  Omit<BaseRadioGroupProps, 'value' | 'onChange'> & {
    onChange?: (value: RadioOptionValueType) => void
  }

export const RadioGroup = <T extends FieldValues>({ control, onChange, name, shouldUnregister, rules, ...props }: RadioGroupProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      shouldUnregister={shouldUnregister}
      rules={rules}
      render={({ field }) => (
        <BaseRadioGroup
          {...props}
          value={field.value}
          onChange={(option) => {
            field.onChange(option)
            onChange?.(option)
          }}
        />
      )}
    />
  )
}
